import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Tooltip } from '@mui/material';
import { createContext, memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Context provided by SearchGridRow so that cell-level components
 * (e.g. the actions column) can open the row's mini menu anchored
 * to a specific DOM element instead of a right-click position.
 */
export interface SearchRowMenuContextValue {
  openMenuAtAnchor: (el: HTMLElement) => void;
}

export const SearchRowMenuContext = createContext<SearchRowMenuContextValue | null>(null);

const useSearchRowMenu = () => {
  const ctx = useContext(SearchRowMenuContext);
  if (!ctx) {
    console.warn('[useSearchRowMenu] Must be used inside SearchRowMenuContext.');
  }
  return ctx;
};

/**
 * Actions cell rendered inside the MUI DataGrid actions column.
 * Clicking the button opens the row-level mini menu (MiniMenuSelector)
 * anchored to this button element.
 */
export const SearchRowActionsCell = memo(() => {
  const { t } = useTranslation();
  const rowMenu = useSearchRowMenu();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      rowMenu?.openMenuAtAnchor(event.currentTarget);
    },
    [rowMenu],
  );

  return (
    <Tooltip title={t('blox.general.more_actions', 'More actions')}>
      <IconButton
        data-testid="search-row-actions-button"
        size="small"
        aria-label={t('blox.general.more_actions', 'More actions')}
        onClick={handleClick}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
});

SearchRowActionsCell.displayName = 'SearchRowActionsCell';
